import type { TableProps } from 'antd'
import { Button, Card, Select, Space, Table } from 'antd'
import { MarketPlaceType, SearchMarketPlaceParams } from 'Modules'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import LinkAddress from 'component/link/link-address'
import { MarketPlaceProvider } from 'context/marketplace/market-place-provider'
import useSearchParams from 'hooks/use-search-params'
import { logError } from 'utils/logger'

import ButtonEnableMarketPlace from './component/button-enable-market-place'

import './style.scss'

export default function Marketplace() {
  const additionalItems = [{ title: 'Marketplace' }]
  const navigate = useNavigate()
  const [data, setData] = useState<MarketPlaceType[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const { searchParams, onChangePagination, onSearch } = useSearchParams<SearchMarketPlaceParams>()

  // Fetch data with pagination
  const getData = async (searchParams?: SearchMarketPlaceParams) => {
    setLoading(true)
    try {
      const res = await AppAdmin.getListMarketplace(searchParams)
      if (res?.data.success) {
        setData(res?.data?.data?.results || [])
        setTotal(res?.data?.data?.totalResults)
      }
    } catch (error) {
      setData([])
      setTotal(0)
      logError('Error fetching data: ', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getData(searchParams)
  }, [searchParams])

  // Define table columns
  const columns: TableProps<any>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'fullName',
      key: 'fullName',
    },
    {
      title: 'Contract',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LinkAddress address={text} />
        </div>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
    },

    {
      title: 'Volume',
      dataIndex: 'volume',
      key: 'volume',
    },
    {
      title: 'Holders',
      dataIndex: 'holders',
      key: 'holders',
    },
    {
      title: 'Yield',
      dataIndex: 'yield',
      key: 'yield',
    },
    {
      title: 'Status',
      dataIndex: 'marketplaceEnable',
      key: 'marketplaceEnable',
      render: (_, record) => {
        return (
          <ButtonEnableMarketPlace
            marketPlace={record}
            onChange={() => {
              getData(searchParams)
            }}
          />
        )
      },
    },
    {
      title: 'Action',
      dataIndex: 'detail',
      key: 'detail',
      render: (_, record) => (
        <Space size="middle">
          <Button
            className="button-detail"
            onClick={() => {
              navigate(`/marketplace/${record._id}`)
            }}
          >
            View
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="main-section user">
      <Breadcrumb additionalItems={additionalItems} />
      <div className="main-title" style={{ marginBottom: 20 }}>
        Marketplace
      </div>
      <div className="user-search">
        <input
          type="text"
          placeholder="Search by name order location..."
          value={searchParams.keyword || ''}
          onChange={(e) =>
            onSearch({
              keyword: e.target.value,
            })
          }
        />
        <Select
          allowClear
          placeholder="By status"
          options={[
            { value: null, label: 'All' },
            { value: true, label: 'Enable' },
            { value: false, label: 'Disable' },
          ]}
          value={searchParams.marketplaceEnable}
          onChange={(value) => {
            onSearch({
              marketplaceEnable: value,
            })
          }}
          style={{ width: '200px', height: '38px', textAlign: 'start' }}
        />
        <Button
          type="primary"
          onClick={() => {
            onSearch()
          }}
        >
          Search
        </Button>
      </div>
      <MarketPlaceProvider>
        <Card style={{ margin: '0 auto' }}>
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey={'_id'}
            pagination={
              total < 11
                ? false
                : {
                    total,
                    current: searchParams.page,
                    pageSize: searchParams.limit,
                  }
            }
            onChange={onChangePagination}
            scroll={{ x: 1000 }}
            bordered
          />
        </Card>
      </MarketPlaceProvider>
    </div>
  )
}
